import React from 'react';

import { ChevronIcon } from '@/assets/icons/chevron';
import KeyboardNavigationContainerDesktop from '@/core/components/dropdown/keyboard-navigation-container-desktop';
import KeyboardNavigationItemDesktop from '@/core/components/dropdown/keyboard-navigation-item-desktop';
import {
    useActiveKeyboardItemName,
    useActiveKeyboardNavigationContainerIdAtom,
} from '@/core/components/dropdown/use-keyboard-navigation-container-desktop';
import useOutsideClick from '@/core/features/event-listener/use-outside-click';
import color from '@/core/features/styles/color';

type DropdownSelectProps = {
    classname?: string;
    keyboardNavigationContainerId: string;
    maxHeightOpened?: number;
    onChange: (value: string) => void;
    options: { title: string }[];
    qaId?: string;
    qaOptionId?: string;
    selectedValue: string;
};

export default function DropdownSelectDesktop({
    classname,
    keyboardNavigationContainerId,
    maxHeightOpened = 185,
    onChange,
    options,
    qaId,
    qaOptionId,
    selectedValue,
}: DropdownSelectProps) {
    const [isOpen, setOpen] = React.useState(false);
    const activeKeyboardItemName = useActiveKeyboardItemName();
    const activeKeyboardNavigationContainerId = useActiveKeyboardNavigationContainerIdAtom();
    const closeDropdown = () => {
        if (!isOpen) {
            return;
        }
        setOpen(() => false);
    };

    const outsideClickRef = useOutsideClick(() => {
        closeDropdown();
    });

    const handleItemClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, itemTitle: string) => {
        event.stopPropagation();
        onChange(itemTitle);
        setOpen(false);
    };

    return (
        <KeyboardNavigationContainerDesktop
            activeOnFocus={true}
            autoFocus={true}
            containerId={keyboardNavigationContainerId}
            internalKey={`${keyboardNavigationContainerId}-${isOpen ? 'opened' : 'closed'}`}
            isActive={isOpen}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
        >
            <div
                className={'select relative'}
                ref={outsideClickRef}
            >
                <div
                    className={`flex-space-between-horizontal border padding-10 border-radius pointer ${classname ?? ''}`}
                    data-qa-id={'qa-dropdown-select'}
                    onClick={() => setOpen(!isOpen)}
                    style={{
                        borderColor: isOpen ? color('lochmara') : color('mineShaft'),
                    }}
                >
                    {selectedValue}
                    <span className={'margin-right-5'}>
                        <ChevronIcon
                            color={color('dustyGray')}
                            height={6}
                            rotate={isOpen ? 0 : 180}
                            width={14}
                        />
                    </span>
                </div>
                {isOpen && (
                    <div
                        className={'dropdownWrapperDropdownSelect width-100 absolute z-index-1 bg-white'}
                        data-qa-id={qaId}
                        style={{ maxHeight: maxHeightOpened }}
                    >
                        {options.map((item, index) => (
                            <KeyboardNavigationItemDesktop
                                key={`${keyboardNavigationContainerId}-item-${index}`}
                                name={`${keyboardNavigationContainerId}-item-${index}`}
                                onSelect={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                    handleItemClick(event, item.title);
                                }}
                            >
                                <div
                                    className={'item padding-10 pointer'}
                                    data-qa-id={qaOptionId}
                                    onClick={(event) => handleItemClick(event, item.title)}
                                    style={{
                                        backgroundColor:
                                            activeKeyboardItemName ===
                                                `${keyboardNavigationContainerId}-item-${index}` &&
                                            activeKeyboardNavigationContainerId === keyboardNavigationContainerId
                                                ? color('athensGray')
                                                : undefined,
                                    }}
                                >
                                    {item.title}
                                </div>
                            </KeyboardNavigationItemDesktop>
                        ))}
                    </div>
                )}
                <style jsx>{`
                    .select {
                        width: 187px;
                    }
                    .dropdownWrapperDropdownSelect {
                        overflow-y: auto;
                        top: 100%;
                    }
                    .item:hover {
                        background-color: ${color('wildSand')};
                    }
                `}</style>
            </div>
        </KeyboardNavigationContainerDesktop>
    );
}
